import { cn } from "@/lib/utils";

interface ExecutiveSummaryProps {
  aiScore: number;
  totalScore: number;
  brandName: string;
  keyword: string;
  competitorMentions?: string[];
  googlePosition: number | null;
  queryUsed: string;
}

const ExecutiveSummary = ({
  aiScore,
  totalScore,
  brandName,
  keyword,
  competitorMentions,
  googlePosition,
  queryUsed,
}: ExecutiveSummaryProps) => {
  if (aiScore !== 0 && aiScore !== 20 && aiScore !== 40) return null;

  const hasCompetitors = competitorMentions && competitorMentions.length > 0;
  const formattedCompetitors = hasCompetitors
    ? competitorMentions!.join(', ').replace(/, ([^,]*)$/, ', and $1')
    : '';

  const config = {
    0: {
      headline: "You're invisible to AI.",
      bg: "bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/30 dark:to-rose-950/20",
      border: "border-red-200 dark:border-red-800/40",
      headlineColor: "text-red-900 dark:text-red-200",
      body: () => {
        const parts: (string | JSX.Element)[] = [
          `When potential customers ask ChatGPT about '${keyword}', your brand doesn't exist.`,
        ];
        if (hasCompetitors) {
          parts.push(' Instead, it actively sends them to ');
          parts.push(<strong key="comp">{formattedCompetitors}</strong>);
          parts.push('.');
        }
        parts.push(' ');
        if (googlePosition && googlePosition <= 10) {
          parts.push(`You rank #${googlePosition} on Google, but that strong search visibility isn't reaching AI platforms yet.`);
        } else if (googlePosition) {
          parts.push(`You currently rank #${googlePosition} on Google, which directly limits your ability to be cited by AI.`);
        } else {
          parts.push("Without a strong search presence, AI platforms lack the foundational data to recommend you.");
        }
        return parts;
      },
    },
    20: {
      headline: "AI knows you. But it's recommending your competitors.",
      bg: "bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/20",
      border: "border-amber-200 dark:border-amber-800/40",
      headlineColor: "text-amber-900 dark:text-amber-200",
      body: () => {
        const parts: (string | JSX.Element)[] = [
          `ChatGPT recognizes ${brandName}, but when users search for '${keyword}', it actively recommends `,
        ];
        if (hasCompetitors) {
          parts.push(<strong key="comp">{formattedCompetitors}</strong>);
        } else {
          parts.push('other brands');
        }
        parts.push(' over you. ');
        if (googlePosition && googlePosition <= 10) {
          parts.push(`Your Google ranking (#${googlePosition}) is strong. This recognition gap is exactly where AI optimization delivers the fastest ROI.`);
        } else if (googlePosition) {
          parts.push(`Improving your Google ranking from #${googlePosition} will be crucial to converting this AI recognition into active recommendations.`);
        } else {
          parts.push("This 'recognized but omitted' gap is exactly where AI visibility optimization has the biggest impact.");
        }
        return parts;
      },
    },
    40: {
      headline: "AI actively recommends you.",
      bg: "bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/20",
      border: "border-emerald-200 dark:border-emerald-800/40",
      headlineColor: "text-emerald-900 dark:text-emerald-200",
      body: () => {
        const parts: (string | JSX.Element)[] = [
          `When users ask ChatGPT about '${keyword}', your brand is front and center. `,
        ];
        if (googlePosition && googlePosition <= 3) {
          parts.push("Combined with your top Google ranking, you have commanding visibility across both traditional and AI search engines.");
        } else if (googlePosition && googlePosition <= 10) {
          parts.push(`Your Google ranking (#${googlePosition}) supports this, but reaching the top 3 could cement your position as the undisputed category leader.`);
        } else {
          parts.push("Keep building your organic search presence to maintain this competitive advantage across all AI platforms.");
        }
        return parts;
      },
    },
  } as const;

  const tier = config[aiScore as 0 | 20 | 40];

  return (
    <div
      className={cn(
        "rounded-xl p-6 border shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500",
        tier.bg,
        tier.border,
      )}
    >
      <h2 className={cn("text-xl font-bold mb-3", tier.headlineColor)}>
        {tier.headline}
      </h2>
      <p className="text-base text-slate-700 dark:text-slate-300 leading-relaxed">
        {tier.body()}
      </p>
    </div>
  );
};

export default ExecutiveSummary;
